import { InventoryLabelData, InventoryPreviewResponse } from '../types/inventoryLabel';
import request from '../../../../../utils/request';

export class InventoryLabelService {
  private async callLabelAPI(
    endpoint: string,
    data: InventoryLabelData,
    options: { preview?: boolean; format?: 'pdf' | 'png' } = {}
  ): Promise<Response> {
    try {
      const response = await request.post(`/api/v1/inventory/inventory-configs/${endpoint}/`, {
        name: data.values.name,
        system_code: data.values.systemCode,
        forecast_code: data.values.forecastCode,
        quantity: data.values.quantity,
        unit_type: data.values.unit_type,
        // 传递显示文案，便于模板使用自定义标题（例如 NIU 覆盖）
        unit_type_display: data.values.unit_type_display,
        template_id: data.templateId,
        content_items: data.values.content_items
      }, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf,image/png,*/*',
          'Content-Type': 'application/json'
        }
      });

      const headers = new Headers();
      Object.entries(response.headers).forEach(([key, value]) => {
        if (value) headers.append(key, value.toString());
      });

      return new Response(response.data, {
        status: response.status,
        statusText: response.statusText,
        headers
      });
    } catch (error) {
      console.error('API调用失败:', error);
      throw error;
    }
  }

  async generatePreview(data: InventoryLabelData): Promise<InventoryPreviewResponse> {
    try {
      const response = await this.callLabelAPI('labels_preview', data, { 
        preview: true,
        format: 'png'
      });
      
      if (!response.ok) {
        throw new Error(`标签生成失败: ${response.statusText}`);
      }

      const blob = await response.blob();
      const contentType = response.headers.get('Content-Type') || '';
      return {
        url: URL.createObjectURL(blob),
        isPDF: contentType.includes('application/pdf')
      };
    } catch (error) {
      console.error('预览生成失败:', error);
      throw error;
    }
  }

  async generatePDF(data: InventoryLabelData): Promise<Blob> {
    try {
      const response = await this.callLabelAPI('labels_generate', data, {
        format: 'pdf'
      });
      
      if (!response.ok) {
        throw new Error(`PDF生成失败: ${response.statusText}`);
      }

      return response.blob();
    } catch (error) {
      console.error('PDF生成失败:', error);
      throw error;
    }
  }

  async generateBatch(dataList: InventoryLabelData[]): Promise<Blob> {
    try {
      const response = await this.callLabelAPI('labels_batch', dataList[0], {
        format: 'pdf'
      });
      
      if (!response.ok) {
        throw new Error(`批量生成失败: ${response.statusText}`);
      }

      return response.blob();
    } catch (error) {
      console.error('批量生成失败:', error);
      throw error;
    }
  }

  // 获取库存单元标签数据
  async getUnitLabels(configId: number) {
    return request.get(`/api/v1/inventory/inventory-configs/${configId}/labels/`);
  }

  // 获取所属项目的系统编码和名称（页面标题用）
  async getProjectInfo(configId: number) {
    return request.get<{ project_code: string; project_name: string }>(`/api/v1/inventory/inventory-configs/${configId}/project_info/`);
  }

  // 下载库存单元标签
  async downloadUnitLabels(configId: number) {
    return request.get(`/api/v1/inventory/inventory-configs/${configId}/labels_download/`, {
      responseType: 'blob'
    });
  }
}

export const inventoryLabelService = new InventoryLabelService(); 